import React from "react";
import { Controller, ControllerFieldState, ControllerRenderProps } from "react-hook-form";

// types
import {
  DynamicFieldOwnProps,
} from "../../types/components/dynamicForms/dynamicField";
import { FieldDescriptor, PhoneValue } from "../../types/components/dynamicForms";

// ui components
import FieldRow from "./FieldRow";
import TextInput from "./inputs/TextInput";
import SelectInput from "./inputs/SelectInput";
import PhoneInput from "./inputs/PhoneInput";
import NumberInput from "./inputs/NumberInput";

/**
 * Resolve the display message from RHF's fieldState error.
 *
 * For compound fields (phone), zodResolver attaches the error to a sub-path
 * (e.g. error.number.message OR error.country.message), not error.message.
 * Read the top-level message first, then fall back to the first nested sub-path
 * that carries one — hardcoding a single sub-path (e.g. "number") silently
 * misses others (e.g. a missing country when the field has no default),
 * blocking submit with no visible message.
 */
export const resolveFieldError = (
  fieldState: ControllerFieldState
): string | undefined => {
  const rawError = fieldState.error as
    | (Record<string, { message?: string } | undefined> & { message?: string })
    | undefined;
  if (!rawError) return undefined;
  if (rawError.message) return rawError.message;
  for (const key of Object.keys(rawError)) {
    if (key === "message" || key === "type" || key === "ref") continue;
    const sub = rawError[key];
    if (sub && typeof sub === "object" && sub.message) return sub.message;
  }
  return undefined;
};

interface RenderControlArgs {
  field: FieldDescriptor;
  rhf: ControllerRenderProps;
  error: string | undefined;
  describedById: string | undefined;
  formatOptionLabel: DynamicFieldOwnProps["formatOptionLabel"];
  suffix: DynamicFieldOwnProps["suffix"];
  inputClassName: DynamicFieldOwnProps["inputClassName"];
}

/** Render the presentational primitive for a descriptor's kind. */
const renderControl = ({
  field,
  rhf,
  error,
  describedById,
  formatOptionLabel,
  suffix,
  inputClassName,
}: RenderControlArgs): React.ReactNode => {
  const { name, kind, options, readOnly, fixedCountry } = field;
  const maxLength = field.constraints?.max;
  const placeholder = field.placeholder ?? "";
  const invalid = Boolean(error);

  switch (kind) {
    case "text":
    case "email":
      return (
        <TextInput
          id={name}
          type={kind === "email" ? "email" : "text"}
          value={(rhf.value as string) ?? ""}
          onChange={rhf.onChange}
          onBlur={rhf.onBlur}
          disabled={readOnly}
          invalid={invalid}
          describedById={describedById}
          maxLength={maxLength}
          placeholder={placeholder}
        />
      );
    case "select":
      return (
        <SelectInput
          id={name}
          value={(rhf.value as string) ?? ""}
          onChange={rhf.onChange}
          onBlur={rhf.onBlur}
          disabled={readOnly}
          invalid={invalid}
          describedById={describedById}
          formatOptionLabel={formatOptionLabel}
          options={options ?? []}
        />
      );
    case "phone":
      return (
        <PhoneInput
          id={name}
          value={rhf.value as PhoneValue}
          onChange={rhf.onChange}
          onBlur={rhf.onBlur}
          disabled={readOnly}
          fixedCountry={fixedCountry}
          invalid={invalid}
          describedById={describedById}
        />
      );
    case "integer":
    case "decimal":
      return (
        <NumberInput
          id={name}
          mode={kind}
          value={(rhf.value as string) ?? ""}
          onChange={rhf.onChange}
          onBlur={rhf.onBlur}
          disabled={readOnly}
          invalid={invalid}
          describedById={describedById}
          maxLength={maxLength}
          placeholder={placeholder}
          suffix={suffix}
          className={inputClassName}
        />
      );
    default: {
      const _exhaustive: never = kind;
      throw new Error(`Unhandled field kind: ${String(_exhaustive)}`);
    }
  }
};

/**
 * The single place react-hook-form and the presentational primitives meet.
 *
 * Reads a FieldDescriptor, owns exactly one Controller, and renders the right
 * primitive by `kind` inside a FieldRow. The primitives stay dumb; Controller's
 * field/fieldState are adapted to the primitive's controlled props by
 * renderControl, and error extraction lives in resolveFieldError.
 *
 * `helpHtml` (server-supplied, may contain markup) renders as the FieldRow
 * sub-label, between the label and the control.
 *
 * `control` is the only RHF type in the component layer and it stops here.
 */
const DynamicField = ({
  field,
  control,
  formatOptionLabel,
  suffix,
  inputClassName,
  belowControl,
}: DynamicFieldOwnProps) => {
  const { name, label, helpHtml } = field;
  const required = field.constraints?.required ?? false;
  const errorId = `${name}-error`;

  const subLabel = helpHtml ? (
    <span dangerouslySetInnerHTML={{ __html: helpHtml }} />
  ) : undefined;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: rhf, fieldState }) => {
        const error = resolveFieldError(fieldState);
        const describedById = error ? errorId : undefined;

        return (
          <FieldRow
            id={name}
            label={label}
            required={required}
            subLabel={subLabel}
            error={error}
            belowControl={belowControl}
          >
            {renderControl({
              field,
              rhf,
              error,
              describedById,
              formatOptionLabel,
              suffix,
              inputClassName,
            })}
          </FieldRow>
        );
      }}
    />
  );
};

export default DynamicField;